<script setup>
import { useI18n } from 'vue-i18n';

import { HrCard, HrImg, HrSkeleton } from '../primitives';
import HrSupportingBtn from './HrSupportingBtn.vue';

/**
 * Catalog tile: an image, a title and a short description, with a link to the item it shows.
 *
 * Built for grids, so every tile in a row lines up whatever its text: the title is held to two
 * lines and the description to three, and the actions sit on the bottom edge.
 *
 * THE CARD ITSELF IS NOT CLICKABLE. "View details" opens the item, beside any action of the caller's,
 * in equal-width columns that stack once they no longer fit. A card that opened the item would
 * nest those actions inside a button, which screen readers flatten into one control and whose title
 * then drops out of the heading list; Material 3 likewise keeps actions off an actionable surface.
 *
 * @status recommended
 * @group cards-dialogs
 * @useWhen Browsing a catalog of items that each have an image, such as store packages
 * @avoidWhen The card groups a section of a page under a heading — use HrContentCard
 * @avoidWhen The items are records to compare or sort — use HrDataTable
 * @related HrContentCard
 * @a11y The title is a heading, so a screen reader can move from card to card by it.
 */
defineOptions({ name: 'HrMediaCard' });

defineProps({
  /**
   * The item's name. Held to two lines.
   * @example Early learner essentials
   */
  title: {
    type: String,
    required: true,
  },
  /** A short description of the item. Held to three lines. */
  supportingText: {
    type: String,
    default: null,
  },
  /**
   * Image shown across the top, at 16:9.
   * @fieldType image
   */
  image: {
    type: String,
    default: null,
  },
  /** Where "View details" leads. Without it the card offers no way to open the item. */
  to: {
    type: [String, Object],
    default: null,
  },
  /** Shows skeletons in place of the image and the text while the item loads. */
  pending: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
</script>

<template>
  <hr-card
    class="hr-media-card"
    variant="outlined"
    shape="md"
  >
    <div class="hr-media-card__media">
      <hr-img
        :src="image ?? ''"
        :ratio="16/9"
        :pending="pending"
      />
      <div
        v-if="$slots['media-attributes']"
        class="hr-media-card__media-attributes"
      >
        <!-- @slot A short HrTag over the image's top corner, for an attribute that cannot sit on it as bare text. -->
        <slot name="media-attributes" />
      </div>
    </div>
    <div class="hr-media-card__body">
      <template v-if="pending">
        <hr-skeleton
          type="text"
          width="70%"
          class="hr-title-medium"
        />
        <hr-skeleton
          type="text"
          width="90%"
        />
      </template>
      <template v-else>
        <h2 class="hr-media-card__title hr-title-medium">
          {{ title }}
        </h2>
        <p
          v-if="supportingText"
          class="hr-media-card__supporting-text hr-body-medium"
        >
          {{ supportingText }}
        </p>
      </template>
    </div>
    <div
      v-if="!pending && (to || $slots.actions)"
      class="hr-media-card__actions"
    >
      <hr-supporting-btn
        v-if="to"
        :to="to"
        :label="t('common.buttonViewDetails')"
      />
      <!-- @slot The item's main action besides opening it, such as a filled HrConfirmBtn. -->
      <slot name="actions" />
    </div>
  </hr-card>
</template>

<style lang="scss" scoped>

.hr-media-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.hr-media-card__media {
  position: relative;
}

.hr-media-card__media-attributes {
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;
  display: flex;
  gap: 8px;
}

.hr-media-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 16px 0;
}

.hr-media-card__title {
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hr-media-card__supporting-text {
  margin: 0;
  color: var(--app-on-surface-variant);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hr-media-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
}

/* An equal basis splits a row evenly, and wraps each to the full width once two no longer fit. */
.hr-media-card__actions > :deep(*) {
  flex: 1 1 128px;
}

/* A lone action keeps the half it would share with a second, so it lines up with the cards beside it. */
.hr-media-card__actions > :deep(*:only-child) {
  flex-grow: 0;
  flex-basis: calc(50% - 4px);
  min-width: 128px;
}

/* A tooltip wrapper can sit between the row and a button, so the width is set on the button too. */
.hr-media-card__actions :deep(.q-btn) {
  width: 100%;
}

</style>
